import "./CodeBlock.scss";
import React from "react";
import { code as codeHighlighter, type HighlightResult, type ThemeInput } from "@streamdown/code";
import { Popper, Paper, ClickAwayListener } from "@mui/material";
import clsx from "clsx";
import { usePopoutWindowContext } from "../../shared/components/PopoutWindow";

/**
 * Renders an arbitrary text fragment — template JSX source, JSON trace/data values, … - as a formatted,
 * syntax-highlighted code block.
 *
 * Uses the same shiki (monokai) setup as the app's Markdown renderer, via @streamdown/code's
 * programmatic `highlight()` API.  The tokens are rendered with inline color styles (not a
 * stylesheet), so the block renders correctly in the Canvas Debugger pop-out even though that
 * window only snapshots the parent's stylesheets once.
 *
 * Memoized so it only re-tokenizes when the code (or layout props) change — this keeps it cheap to use
 * inside the cursor-following hover tooltips (flame graph, grid value cells), which re-render on every
 * pointer move.  Grammars are lazy-loaded once and token results are cached inside the plugin, so
 * repeated highlights of the same content are synchronous.
 */
type Props = {
    code: string;
    /** Prism grammar to highlight with — "jsx" for template fragments, "json" for trace/data values. */
    language?: string;
    maxHeight?: number;
    fontSize?: string;
    /** Collapse to a single clipped line (whitespace-collapsed) — for rendering inside a table cell. */
    singleLine?: boolean;
    /**
     * Clicking anywhere on the block pins the full content open in a persistent popper, so it can be
     * scrolled and text-selected — unlike the transient hover tooltip, which is pointer-events: none.
     * Dismissed by clicking the block again, clicking outside the popper, or pressing Escape.
     * Intended for `singleLine` grid cells.
     */
    pinnable?: boolean;
    /** Notifies the host cell when the pinned popper opens/closes — e.g. to hide its hover tooltip. */
    onPinToggle?: (pinned: boolean) => void;
};

const PINNED_POPPER_MODIFIERS = [
    { name: "offset", options: { offset: [0, 4] } },
    { name: "preventOverflow", options: { padding: 8 } },
    { name: "flip", enabled: true },
];

// Swallow mouse moves so the host cell's cursor-following hover tooltip doesn't fight the pinned popper.
const stopMove = (e: React.MouseEvent) => e.stopPropagation();

const THEMES: [ThemeInput, ThemeInput] = ["monokai", "monokai"];
// Monokai's background/foreground, applied directly so the block looks right even while the
// grammar is still loading (plain-text fallback)...
const THEME_BACKGROUND = "#272822";
const THEME_FOREGROUND = "#f8f8f2";
const FONT_FAMILY = `Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace`;

// Highlights the given code into themed tokens.  `highlight()` returns the tokens synchronously
// once the grammar is cached, or null while it loads (the callback then fires exactly once)...
function useHighlightedTokens(content: string, language: string): HighlightResult | null {
    const [result, setResult] = React.useState<HighlightResult | null>(null);

    React.useLayoutEffect(() => {
        // `supportsLanguage` validates the plain string against shiki's bundled-language union...
        const bundledLanguage = language as Parameters<typeof codeHighlighter.highlight>[0]["language"];
        if (!codeHighlighter.supportsLanguage(bundledLanguage)) {
            setResult(null);
            return;
        }

        let cancelled = false;
        const syncResult = codeHighlighter.highlight(
            { code: content, language: bundledLanguage, themes: THEMES },
            asyncResult => { if (!cancelled) setResult(asyncResult); });
        setResult(syncResult);
        return () => { cancelled = true; };
    }, [content, language]);

    return result;
}

function CodeBlock({ code, language = "jsx", maxHeight = 240, fontSize = "0.72rem", singleLine = false, pinnable = false, onPinToggle }: Props) {
    const [pinned, setPinned] = React.useState(false);
    const anchorRef = React.useRef<HTMLSpanElement>(null);
    // Portal the pinned popper into the pop-out window's body so it renders in the correct window.
    const container = usePopoutWindowContext()?.window?.document.body ?? undefined;

    const togglePin = React.useCallback((e: React.MouseEvent) => {
        // The popper is portaled out of the DOM tree but is still a React child of the anchor span, so
        // clicks inside it bubble here through the React tree — those must not unpin (the content has to
        // stay open for text selection/copying). Only clicks landing on the chip itself toggle.
        if (!anchorRef.current?.contains(e.target as Node)) return;
        e.stopPropagation();
        const next = !pinned;
        setPinned(next);
        onPinToggle?.(next);
    }, [pinned, onPinToggle]);

    const unpin = React.useCallback(() => {
        setPinned(false);
        onPinToggle?.(false);
    }, [onPinToggle]);

    // Dismiss on click-away — except clicks within the anchor chip, whose pin button already toggles
    // (handling both would double-fire and re-open the popper).
    const handleClickAway = React.useCallback((event: MouseEvent | TouchEvent) => {
        if (anchorRef.current?.contains(event.target as Node)) return;
        unpin();
    }, [unpin]);

    // Dismiss on Escape. Listen on the owning document — in the Canvas Debugger that's the pop-out window's.
    React.useEffect(() => {
        if (!pinned) return;
        const doc = anchorRef.current?.ownerDocument ?? document;
        const onKeyDown = (e: KeyboardEvent) => e.key === "Escape" && unpin();
        doc.addEventListener("keydown", onKeyDown);
        return () => doc.removeEventListener("keydown", onKeyDown);
    }, [pinned, unpin]);

    const content = singleLine
        ? code?.replace(/\s+/g, " ").trim() || "(empty)"
        : code?.trim() || "(empty)";

    const highlighted = useHighlightedTokens(content, language);

    const containerStyle: React.CSSProperties = {
        background: highlighted?.bg ?? THEME_BACKGROUND,
        color: highlighted?.fg ?? THEME_FOREGROUND,
        fontFamily: FONT_FAMILY,
        ...(singleLine
            ? {
                margin: 0,
                padding: "0 6px",
                borderRadius: 3,
                fontSize,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
            }
            : {
                margin: 0,
                padding: 8,
                borderRadius: 4,
                fontSize,
                maxHeight,
                overflow: "auto",
            }),
    };

    const highlighter = (
        <div style={containerStyle}>
            <pre style={{ margin: 0, fontFamily: "inherit", fontSize: "inherit", whiteSpace: singleLine ? "nowrap" : "pre-wrap", overflowWrap: "break-word" }}>
                {highlighted
                    ? highlighted.tokens.map((line, lineIndex) => (
                        <React.Fragment key={lineIndex}>
                            {line.map((token, tokenIndex) =>
                                <span key={tokenIndex} style={{ color: token.color, backgroundColor: token.bgColor, ...token.htmlStyle as React.CSSProperties }}>{token.content}</span>)}
                            {lineIndex < highlighted.tokens.length - 1 ? "\n" : null}
                        </React.Fragment>
                    ))
                    : content}
            </pre>
        </div>
    );

    if (!pinnable) return highlighter;

    return (
        <span
            className={clsx("code-block-pinnable", pinned && "pinned")}
            ref={anchorRef}
            onClick={togglePin}
            onMouseMove={pinned ? stopMove : undefined}
        >
            {highlighter}

            {/* Kept mounted (and interactive) until the block is clicked again, a click lands outside, or
                Escape is pressed — unlike the transient hover tooltip. Click-away uses onMouseDown so a
                text selection that starts inside the popper can be dragged/released outside it. */}
            <Popper
                open={pinned}
                anchorEl={anchorRef.current}
                container={container}
                placement="bottom-start"
                modifiers={PINNED_POPPER_MODIFIERS}
                style={{ zIndex: 2000 }}
            >
                <ClickAwayListener onClickAway={handleClickAway} mouseEvent="onMouseDown">
                    <Paper elevation={6} sx={{ p: 0, overflow: "hidden", borderRadius: 1, maxWidth: 640 }}>
                        <CodeBlock code={code} language={language} maxHeight={320} />
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </span>
    );
}

export default React.memo(CodeBlock);
